function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    let button = document.getElementById('Facets-Toggle');

    // Add functionality to buttons
    if (button && this.container) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.getElementsByTagName("body")[0].classList.toggle('open-cc');
        this.container.classList.toggle('active');
      });
    }

  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Delegated: the per-filter "Reset" link is re-rendered after every update, so a
    // listener attached to the link itself would be lost.
    this.addEventListener('click', (event) => {
      const reset = event.target.closest('.facets__reset');
      if (!reset) return;
      event.preventDefault();
      FacetFiltersForm.navigate(reset.href);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    const onResize = debounce((event) => {
      let root = document.documentElement,
        header = document.querySelector('.header');

      if (header && window.innerWidth <= 768) {
        root.style.setProperty('--header-height-mobile', header.clientHeight + "px");
      }
    }, 200);
    window.addEventListener('resize', onResize);
    window.dispatchEvent(new Event('resize'));
  }

  // Applies the query string of a filter / chip / reset / "clear all" link without a page load.
  static navigate(href) {
    const queryStart = href.indexOf('?');
    FacetFiltersForm.renderPage(queryStart === -1 ? '' : href.slice(queryStart + 1));
  }

  // Plain page load, used whenever a partial (ajax) render is not possible.
  static hardNavigate(searchParams) {
    window.location.href = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
  }

  // #product-grid only exists while there are products, so it can't be the only source of
  // the section id - otherwise a filter combination with 0 results would lock the filters.
  static getSectionId() {
    const grid = document.getElementById('product-grid');
    if (grid && grid.dataset.id) return grid.dataset.id;

    const container = document.getElementById('ProductGridContainer');
    const section = container ? container.closest('[id^="shopify-section-"]') : null;
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sectionId = FacetFiltersForm.getSectionId();
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!sectionId || !gridContainer) {
      FacetFiltersForm.hardNavigate(searchParams);
      return;
    }

    const collection = gridContainer.querySelector('.collection');
    if (collection) collection.classList.add('loading');
    for (const item of document.getElementsByClassName('thb-filter-count')) {
      item.classList.add('loading');
    }

    const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
    FacetFiltersForm.latestUrl = url;

    const cached = FacetFiltersForm.filterData.find(element => element.url === url);
    if (cached) {
      FacetFiltersForm.renderResponse(cached.html, event, searchParams);
    } else {
      FacetFiltersForm.fetchSection(url, event, searchParams);
    }

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static fetchSection(url, event, searchParams) {
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        // A newer filter change has already been requested; don't overwrite it with this older result.
        if (FacetFiltersForm.latestUrl !== url) return;
        FacetFiltersForm.renderResponse(html, event, searchParams);
      })
      .catch(() => FacetFiltersForm.hardNavigate(searchParams));
  }

  static renderResponse(html, event, searchParams) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsedHTML.getElementById('ProductGridContainer');

    // Fall back to a normal page load if the response is unusable, or if the set of filter
    // groups changed (a group can appear/disappear depending on what is selected).
    if (!newGrid || FacetFiltersForm.filterGroupsChanged(parsedHTML)) {
      FacetFiltersForm.hardNavigate(searchParams);
      return;
    }

    FacetFiltersForm.renderFilters(parsedHTML, event);
    document.getElementById('ProductGridContainer').innerHTML = newGrid.innerHTML;
    FacetFiltersForm.renderProductCount(parsedHTML);
  }

  static filterGroupsChanged(parsedHTML) {
    const selector = '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter';
    const indexes = (root) => Array.from(root.querySelectorAll(selector)).map(el => el.dataset.index).sort().join('|');
    return indexes(document) !== indexes(parsedHTML);
  }

  static renderProductCount(parsedHTML) {
    const countElement = parsedHTML.getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    for (const item of container) {
      if (countElement) item.innerHTML = countElement.innerHTML;
      item.classList.remove('loading');
    }
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');

    // The group the user just changed keeps its markup (so it stays open and keeps its
    // scroll position); every other group is refreshed with the new availability counts.
    const activeFilter = event && event.target && event.target.closest ? event.target.closest('.js-filter') : null;
    const matchesIndex = (element) => activeFilter ? element.dataset.index === activeFilter.dataset.index : false;
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && activeFilter) FacetFiltersForm.renderCounts(countsToRender, activeFilter);
  }

  static renderActiveFacets(parsedHTML) {
    const source = parsedHTML.querySelector('.active-facets');
    const target = document.querySelector('.active-facets');
    if (source && target) target.innerHTML = source.innerHTML;
  }

  static renderAdditionalElements(parsedHTML) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      const source = parsedHTML.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
  }

  // Refreshes the footer of the group being edited: its "n selected" text AND its Reset
  // link, whose href otherwise keeps pointing at the URL from before the change.
  static renderCounts(source, target) {
    const sourceFooter = source.querySelector('.thb-filter-dropdown__footer');
    const targetFooter = target.querySelector('.thb-filter-dropdown__footer');

    if (sourceFooter && targetFooter) {
      targetFooter.outerHTML = sourceFooter.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);

    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.latestUrl = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    // Delegated: the chips and "Clear all" are re-rendered after every filter change.
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link || !this.contains(link)) return;
      event.preventDefault();
      FacetFiltersForm.navigate(link.href);
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount');

    // noUiSlider throws when min == max (e.g. a result set with a single price)
    if (!slider || !(parseFloat(slider.dataset.max) > 0)) return;

    let args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

window.addEventListener('load', () => {
  new FacetsToggle();
});
